import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  User, MapPin, Phone, Mail, PencilLine, CheckCircle2, Loader2, Send,
  FileText, Upload, X, Building2, ClipboardList,
} from "lucide-react";
import { useI18n } from "@/App";

const API = `/api`;
const STORE_KEY = "bz_user_profile";
const MAX_MB = 15;

const readProfile = () => {
  try { return JSON.parse(localStorage.getItem(STORE_KEY) || "null") || {}; }
  catch { return {}; }
};
const saveProfile = (p) => { try { localStorage.setItem(STORE_KEY, JSON.stringify(p)); } catch {} };

export default function SubsidyModal({ subsidy, open, onOpenChange }) {
  const { lang } = useI18n();
  const [form, setForm] = useState({ name: "", phone: "", location: "", company: "", email: "", notes: "" });
  const [files, setFiles] = useState([]); // { file, docLabel }
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (open) {
      const saved = readProfile();
      setForm({
        name: saved.name || "",
        phone: saved.phone || "",
        location: saved.address || saved.location || "",
        company: saved.company || "",
        email: saved.email || "",
        notes: "",
      });
      setFiles([]);
      setSuccess(false);
      setSubmitting(false);
    }
  }, [open]);

  const docList = useMemo(() => subsidy?.docs || [], [subsidy]);

  if (!subsidy) return null;
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const onFileAdd = (docLabel, fileList) => {
    const chosen = Array.from(fileList || []);
    const accepted = [];
    for (const f of chosen) {
      if (f.size > MAX_MB * 1024 * 1024) {
        toast.error(`${f.name} > ${MAX_MB}MB`);
        continue;
      }
      accepted.push({ file: f, docLabel });
    }
    setFiles((prev) => [...prev, ...accepted]);
  };

  const removeFile = (idx) => setFiles((p) => p.filter((_, i) => i !== idx));

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.name.trim() || !form.phone.trim() || !form.location.trim()) {
      toast.error(lang === "HI" ? "नाम, फ़ोन और पता ज़रूरी है" : "Name, Phone and Location are required");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("category", subsidy.key);
      fd.append("name", form.name.trim());
      fd.append("phone", form.phone.trim());
      fd.append("location", form.location.trim());
      if (form.company) fd.append("company", form.company.trim());
      if (form.email) fd.append("email", form.email.trim());
      // Include a doc-label map inside notes so admin knows which doc each file corresponds to
      const docMap = files.map((f) => `- ${f.docLabel}: ${f.file.name}`).join("\n");
      const finalNotes = [
        form.notes.trim(),
        docMap ? `\n\n[Docs mapping]\n${docMap}` : "",
      ].filter(Boolean).join("");
      fd.append("notes", finalNotes);
      files.forEach((f) => fd.append("files", f.file, f.file.name));

      await axios.post(`${API}/subsidy-submissions`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      saveProfile({
        name: form.name, phone: form.phone, address: form.location,
        location: form.location, company: form.company, email: form.email,
      });
      setSuccess(true);
      toast.success(lang === "HI" ? "आपकी सब्सिडी रिक्वेस्ट सबमिट हो गई" : "Subsidy request submitted");
      setTimeout(() => onOpenChange(false), 1800);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const Icon = ({ children }) => (
    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">{children}</span>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="subsidy-modal"
        className="bg-white sm:max-w-3xl max-h-[92vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle>
            <span className="text-emerald-700 text-xs tracking-widest uppercase font-semibold">
              {lang === "HI" ? "सरकारी सब्सिडी क्लेम" : "Claim Govt. Subsidy"}
            </span>
            <div className="font-display text-2xl mt-1 text-slate-900">
              {lang === "HI" ? subsidy.hi : subsidy.en}
            </div>
          </DialogTitle>
          <DialogDescription>
            <span className="text-slate-500 text-sm">{subsidy.tagline}</span>
          </DialogDescription>
        </DialogHeader>

        {success ? (
          <div className="flex flex-col items-center py-10 text-center" data-testid="subsidy-success">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
              <CheckCircle2 className="w-9 h-9 text-emerald-600" />
            </div>
            <p className="text-lg font-display mt-3">
              {lang === "HI" ? "रिक्वेस्ट सबमिट हो गई" : "Request submitted"}
            </p>
            <p className="text-sm text-slate-500 mt-2">
              {lang === "HI" ? "हमारी टीम 24 घंटे में संपर्क करेगी।" : "Our team will contact you within 24 hours."}
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="subsidy-form">
            {/* User info */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="relative">
                <input data-testid="subsidy-name" placeholder={lang === "HI" ? "नाम" : "Full Name"}
                  value={form.name} onChange={(e) => set("name", e.target.value)}
                  className="bz-input pr-10 bg-slate-50" required />
                <Icon><User className="w-4 h-4" /></Icon>
              </div>
              <div className="relative">
                <input data-testid="subsidy-phone" placeholder={lang === "HI" ? "फ़ोन नंबर" : "Phone Number"}
                  value={form.phone} onChange={(e) => set("phone", e.target.value)}
                  className="bz-input pr-10 bg-slate-50" required />
                <Icon><Phone className="w-4 h-4" /></Icon>
              </div>
              <div className="relative">
                <input data-testid="subsidy-company" placeholder={lang === "HI" ? "कंपनी / फ़र्म" : "Company / Firm"}
                  value={form.company} onChange={(e) => set("company", e.target.value)}
                  className="bz-input pr-10 bg-slate-50" />
                <Icon><Building2 className="w-4 h-4" /></Icon>
              </div>
              <div className="relative">
                <input data-testid="subsidy-location" placeholder={lang === "HI" ? "स्थान / राज्य" : "Location / State"}
                  value={form.location} onChange={(e) => set("location", e.target.value)}
                  className="bz-input pr-10 bg-slate-50" required />
                <Icon><MapPin className="w-4 h-4" /></Icon>
              </div>
              <div className="relative sm:col-span-2">
                <input data-testid="subsidy-email" type="email"
                  placeholder={lang === "HI" ? "ईमेल (वैकल्पिक)" : "Email (optional)"}
                  value={form.email} onChange={(e) => set("email", e.target.value)}
                  className="bz-input pr-10 bg-slate-50" />
                <Icon><Mail className="w-4 h-4" /></Icon>
              </div>
            </div>

            {/* Notes */}
            <div className="relative">
              <textarea data-testid="subsidy-notes"
                placeholder={lang === "HI" ? "अतिरिक्त जानकारी" : "Additional information / notes"}
                value={form.notes} onChange={(e) => set("notes", e.target.value)}
                className="bz-input min-h-[80px] pr-10 bg-slate-50" />
              <Icon><PencilLine className="w-4 h-4" /></Icon>
            </div>

            {/* Required documents checklist with per-doc uploader */}
            <div className="rounded-xl border border-emerald-100 bg-emerald-50/40 p-4">
              <div className="flex items-center gap-2 mb-3">
                <ClipboardList className="w-4 h-4 text-emerald-700" />
                <div className="text-sm font-semibold text-slate-800">
                  {lang === "HI" ? "ज़रूरी डाक्यूमेंट्स" : "Required documents"}
                  <span className="text-slate-400 font-normal ml-1">({docList.length})</span>
                </div>
              </div>
              <ul className="space-y-2 max-h-64 overflow-y-auto pr-1">
                {docList.map((doc, i) => (
                  <li key={i} className="flex items-center justify-between gap-3 bg-white border border-emerald-100 rounded-lg px-3 py-2">
                    <div className="flex items-start gap-2 text-sm text-slate-700 flex-1">
                      <span className="text-emerald-700 font-semibold w-6 shrink-0">{i + 1}.</span>
                      <span className="leading-snug">{doc}</span>
                    </div>
                    <label
                      data-testid={`subsidy-doc-upload-${i}`}
                      className="cursor-pointer text-xs font-semibold text-emerald-700 hover:text-emerald-800 inline-flex items-center gap-1 border border-emerald-300 rounded-full px-3 py-1 bg-white hover:bg-emerald-50 shrink-0"
                    >
                      <Upload className="w-3 h-3" />
                      {lang === "HI" ? "अपलोड" : "Upload"}
                      <input
                        type="file"
                        multiple
                        accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx"
                        className="hidden"
                        onChange={(e) => { onFileAdd(doc, e.target.files); e.target.value = ""; }}
                      />
                    </label>
                  </li>
                ))}
              </ul>

              {/* Generic uploader */}
              <label
                data-testid="subsidy-doc-upload-other"
                className="mt-3 cursor-pointer inline-flex items-center gap-2 text-xs font-semibold text-slate-700 border border-dashed border-slate-300 rounded-lg px-3 py-2 bg-white hover:border-emerald-500 hover:text-emerald-700"
              >
                <Upload className="w-4 h-4" />
                {lang === "HI" ? "अन्य दस्तावेज़ अपलोड करें" : "Upload other supporting docs"}
                <input
                  type="file"
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx"
                  className="hidden"
                  onChange={(e) => { onFileAdd("Other", e.target.files); e.target.value = ""; }}
                />
              </label>

              {/* Selected files */}
              {files.length > 0 && (
                <div className="mt-4">
                  <div className="text-xs font-semibold text-slate-600 mb-2">
                    {lang === "HI" ? "चुनी गई फ़ाइलें" : "Selected files"} ({files.length})
                  </div>
                  <ul className="space-y-1.5">
                    {files.map((f, i) => (
                      <li key={i}
                        data-testid={`subsidy-file-chip-${i}`}
                        className="flex items-center justify-between gap-2 bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-xs">
                        <div className="flex items-center gap-2 text-slate-700 min-w-0">
                          <FileText className="w-4 h-4 text-emerald-600 shrink-0" />
                          <span className="truncate">
                            <span className="font-semibold text-slate-800">{f.docLabel}:</span> {f.file.name}
                          </span>
                          <span className="text-slate-400 shrink-0">
                            {(f.file.size / 1024).toFixed(0)} KB
                          </span>
                        </div>
                        <button type="button" onClick={() => removeFile(i)}
                          data-testid={`subsidy-file-remove-${i}`}
                          className="text-slate-400 hover:text-red-600">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <p className="mt-3 text-[11px] text-slate-500 leading-snug">
                {lang === "HI"
                  ? `PDF, JPG, PNG, DOC, XLS — प्रति फ़ाइल अधिकतम ${MAX_MB}MB।`
                  : `PDF, JPG, PNG, DOC, XLS — Max ${MAX_MB}MB per file.`}
              </p>
            </div>

            <button type="submit" data-testid="subsidy-submit" disabled={submitting}
              className="w-full rounded-xl py-4 font-bold text-white text-base tracking-wide inline-flex items-center justify-center gap-2 transition"
              style={{ background: submitting ? "#059669aa" : "#059669", boxShadow: "0 12px 28px -12px rgba(5,150,105,0.55)" }}>
              {submitting ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> {lang === "HI" ? "भेजा जा रहा है…" : "SUBMITTING…"}</>
              ) : (
                <><Send className="w-4 h-4" /> {lang === "HI" ? "सब्सिडी रिक्वेस्ट भेजें" : "SUBMIT SUBSIDY REQUEST"}</>
              )}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
